import type * as pf from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { PARAM } from '@amirada/lib-wilo-shared'
import { ut_wilo_isInternal, ut_wilo_isInternalParamData } from '~/utils/utils'

// The `custom` params of ONE request, derived from paramData and nothing else.
//
// ⚑ Two callers rely on this: customerModifyQuery (what the request carries) and
// ut_createParamDataString (what the stores' cache keys carry). The second runs on EVERY store read,
// render included, so this must not touch a Nuxt composable — there is no instance to bind to there.
// Everything it needs is on paramData, host included (put there by the routing middleware).
export const customerBuildCustom: tp.TCustomerBuildCustom = (paramData) => {
  const custom: pfWilo.WiloCustom = {}

  // Internal client (host) OR internal print (param9=1) → lift the price-prop restriction.
  if (ut_wilo_isInternalParamData(paramData)) { custom.int = true }

  // Price-list context (param16) → keep the columns flagged list_feature_visible, which every other
  // answer has removed. A paramN carries it because the PDF is rendered by a headless browser that
  // knows nothing but the url — the same reason internalPrint exists.
  if (String(paramData[PARAM.priceList] ?? '') === '1') { custom.klc = true }

  // WiloCustom stays the authoring type — it catches a mistyped flag, which a bare TCustom would
  // wave through. The cast is only about the shape of the declaration: TCustom is a
  // Record<string, …>, and an interface carries no implicit index signature (a type alias would).
  // Safe by construction, since every field above is assigned a boolean.
  return Object.keys(custom).length ? custom as pf.TCustom : undefined
}

// Customer-specific, per-call query modification (Wilo).
// Runs before every pfinder request; the decision is made from the data/query context.
export const customerModifyQuery: tp.TCustomerModifyQuery = (paramsQuery, paramData) => {
  const custom: pfWilo.WiloCustom = { ...(customerBuildCustom(paramData) || {}) }

  // ⚑ The one thing the pure hook above leaves out: NUXT_PUBLIC_IS_INTERNAL, the build override that
  // makes a dev server behave like the internal one. It is added HERE because this path has a Nuxt
  // instance, and it is left out THERE because a value that is fixed for a whole deployment cannot
  // tell two requests of one session apart — which is the only thing a cache key is for.
  if (ut_wilo_isInternal()) { custom.int = true }

  if (Object.keys(custom).length) { paramsQuery.custom = custom as pf.TCustom }
}
